/** @file Utilities for parsing, formatting, and validating CIDR notations. */
/** Invalid input message. */
export const ERROR_INVALID_INPUT = 'Please enter an IP address or a CIDR notation.';
/** Invalid IPV4 address message. */
export const ERROR_INVALID_IPV4_ADDR = 'Invalid IP v4 address.';
/** Invalid IPV4 address mask message. */
export const ERROR_INVALID_MASK = 'Invalid mask.';
/**
 * Take a CIDR notation string for a single IP and formats it without the
 * additional range information.
 *
 * Example: 128.0.0.0/32 becomes 128.0.0.0.
 * Example: 128.0.0.0/27 remains the same.
 *
 * @param cidrFormattedIp An IP address which may be in CIDR format.
 * @returns The formatted address, showing CIDR suffix when it's not a single IP address.
 */
export function formatForHuman(cidrFormattedIp) {
    const trimmed = cidrFormattedIp.trim();
    return trimmed.endsWith('/32') ? trimmed.slice(0, -3) : trimmed;
}
/**
 * Converts an integer to an IPv4 address.
 *
 * @param n Number to convert.
 * @returns An IP address as a string.
 */
export function int2ip(n) {
    return [(n >> 24) & 255, (n >> 16) & 255, (n >> 8) & 255, n & 255].join('.');
}
/**
 * Converts an IPv4 address to an integer.
 *
 * @param ip IPv4 address as a string.
 * @returns An integer represetation of that IP address.
 */
export function ip2int(ip) {
    const parts = ip.split('.').map(n => parseInt(n, 10));
    if (parts.length !== 4 || parts.some(part => Number.isNaN(part))) {
        throw new Error(ERROR_INVALID_IPV4_ADDR);
    }
    return ((parts[0] * 256 + parts[1]) * 256 + parts[2]) * 256 + parts[3];
}
/** A class to intepretet string as a CIDR notation for IPv4. */
export class IPv4Notation {
    /** Number of bits in the network portion of the address for this block. */
    bitmask;
    /** The network address as a number. */
    baseLong;
    /** The mask as a number. */
    maskAsInt;
    /** The address of the network block as a string. */
    base;
    /** The number of IP address in the block. */
    size;
    /** First usable address in the block. */
    first;
    /** Last usable address in the block. */
    last;
    /**
     * Constructor of the class.
     *
     * @param input The string to be parsed.
     */
    constructor(input) {
        if (input === '') {
            throw new Error(ERROR_INVALID_INPUT);
        }
        const [address, strMask] = input.split('/', 2);
        if (strMask === undefined) {
            this.bitmask = 32;
        }
        else {
            this.bitmask = parseInt(strMask, 10);
        }
        if (Number.isNaN(this.bitmask) || this.bitmask < 0 || this.bitmask > 32) {
            throw new Error(ERROR_INVALID_MASK);
        }
        this.maskAsInt = this.bitmask === 0 ? 0 : (0xffffffff << (32 - this.bitmask)) >>> 0;
        this.baseLong = (ip2int(address) & this.maskAsInt) >>> 0;
        this.base = int2ip(this.baseLong);
        this.size = 2 ** (32 - this.bitmask);
        this.first = this.base;
        this.last = int2ip((this.baseLong | ~this.maskAsInt) >>> 0);
    }
}
